import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { HERO_SHELL, HERO_TONE, type HeroTone, useHeroFormat } from './loanHero'

/** Good money on the left (repaid, owed to you), the other side on the right. */
export function HeroSplitBar({
  good,
  bad,
  className,
}: {
  good: number
  bad: number
  className?: string
}) {
  if (good <= 0 && bad <= 0) return null
  return (
    <div
      aria-hidden="true"
      className={cn('flex h-1.5 gap-0.5 overflow-hidden rounded md:h-2', className)}
    >
      {good > 0 && <div className="bg-[var(--fl-hero-bar)]" style={{ flex: good }} />}
      {bad > 0 && <div className="bg-[var(--fl-hero-expense)] opacity-70" style={{ flex: bad }} />}
    </div>
  )
}

export interface LoansHeroItem {
  label: string
  amount: number
  tone?: HeroTone
  sub?: ReactNode
}

/** The Loans page summary for one tab: a headline figure, its split, and up to three side figures. */
export function LoansHero({
  ariaLabel,
  label,
  amount,
  tone,
  split,
  note,
  items,
}: {
  ariaLabel: string
  label: string
  amount: number
  tone?: HeroTone
  split?: { good: number; bad: number }
  note?: string
  items: LoansHeroItem[]
}) {
  const format = useHeroFormat()
  return (
    <section
      aria-label={ariaLabel}
      className={cn(
        HERO_SHELL,
        'grid gap-2 motion-safe:animate-fade-slide-up md:gap-4 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:items-end lg:gap-7'
      )}
    >
      <div>
        <div className="flex items-baseline justify-between gap-3 md:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[13px] md:font-normal md:normal-case md:tracking-normal">
            {label}
          </p>
          <p
            data-testid="loans-hero-amount"
            className={cn(
              'whitespace-nowrap font-serif text-[22px] font-bold leading-tight tracking-tight tabular-nums md:mt-1 md:text-[42px] md:font-semibold',
              tone && HERO_TONE[tone]
            )}
          >
            {format(amount)}
          </p>
        </div>
        {split && <HeroSplitBar {...split} className="mt-2 md:mt-3" />}
        {note && (
          <p className="mt-1.5 hidden text-xs text-[var(--fl-hero-fg-2)] md:block">{note}</p>
        )}
      </div>
      <dl className="grid grid-cols-3 gap-x-2.5 border-t border-[var(--fl-hero-bar-faint)] pt-2 md:gap-x-3 md:border-0 md:pt-0 lg:contents">
        {items.map((item) => (
          <div key={item.label} className="min-w-0">
            <dt className="text-[11.5px] leading-tight text-[var(--fl-hero-fg-2)] md:text-[13px]">
              {item.label}
            </dt>
            <dd
              className={cn(
                'whitespace-nowrap text-[13px] font-semibold tabular-nums md:mt-0.5 md:font-serif md:text-2xl',
                item.tone && HERO_TONE[item.tone]
              )}
            >
              {format(item.amount)}
            </dd>
            {item.sub && (
              <dd className="text-[11px] leading-tight text-[var(--fl-hero-fg-2)] md:text-xs">
                {item.sub}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </section>
  )
}
